import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { stringify } from "qs";

import type { ArtistClient } from "db/schemas/artist";
import type { SongClient } from "db/schemas/song";
import type { StatSong } from "db/schemas/statSong";
import type { UserClient } from "db/schemas/user";
import type { UserSong } from "db/schemas/userSong";
import type { ArtistsState } from "features/artistsSlice";
import { selectToken } from "features/auth/authSlice";
import type { SongsState } from "features/songs/songsSlice";
import type { UsersState } from "features/usersSlice";
import type { AppState } from "store/store";

import { providesList } from "./providesList";

export type Post<T extends { id: any }> = Partial<T> & Omit<T, "id">;
export type Patch<T extends { id: any }> = Partial<T> & Pick<T, "id">;

// Define a service using a base URL and expected endpoints
export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: fetchBaseQuery({
    // Need absolute URL for SSR
    baseUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/api/admin`,
    prepareHeaders: (headers, { getState }) => {
      const token = selectToken(getState() as AppState);
      // If we have a token set in state, let's assume that we should be passing it.
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Artists", "Songs", "Users"],
  endpoints: (builder) => ({
    getSong: builder.query<UserSong, string>({
      query: (id) => {
        return `song/${id}`;
      },
      providesTags: (_result, _error, id) => [{ type: "Songs", id }],
    }),
    updateSong: builder.mutation<SongClient, Patch<SongClient>>({
      query: ({ id, ...patch }) => ({
        url: `song/${id}`,
        method: "PATCH",
        body: patch,
      }),
      // Invalidates all queries that subscribe to this Song `id` only.
      // `getSong` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id }) => [{ type: "Songs", id }],
    }),
    deleteSong: builder.mutation<void, string>({
      query: (id) => ({
        url: `song/${id}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this Song `id` only.
      // `getSong` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, id) => [{ type: "Songs", id }],
    }),
    getSongs: builder.query<
      Array<UserSong>,
      Pick<SongsState, "search" | "sortBy" | "sortDirection"> & {
        filter?: { artistId: string };
      }
    >({
      query: ({ filter, search, sortBy, sortDirection }) => {
        const query = {
          // limit: perPage,
          // offset: rangeStart,
          sortBy,
          sortDirection,
          filter: { ...filter, q: search },
        };
        return `songs?${stringify(query)}`;
      },
      providesTags: (result) => providesList(result, "Songs"),
    }),
    // this is definitely not the right idea, but keeping it in here
    // temporarily in order to be a placeholder on what has to be added
    // this might become the getStatsSongs idea
    getStatSongs: builder.query<
      Array<StatSong>,
      Pick<SongsState, "search" | "sortBy" | "sortDirection"> & {
        filter?: { artistId: string };
      }
    >({
      query: ({ filter, search, sortBy, sortDirection }) => {
        const query = {
          // limit: perPage,
          // offset: rangeStart,
          sortBy,
          sortDirection,
          filter: { ...filter, q: search },
        };
        return `songs?${stringify(query)}`;
      },
      // hmm
      //providesTags: (result) => providesList(result, "Songs"),
    }),
    getArtist: builder.query<ArtistClient, string>({
      query: (id) => {
        return `artist/${id}`;
      },
      providesTags: (_result, _error, id) => [{ type: "Artists", id }],
    }),
    updateArtist: builder.mutation<ArtistClient, Patch<ArtistClient>>({
      query: ({ id, ...patch }) => ({
        url: `artist/${id}`,
        method: "PATCH",
        body: patch,
      }),
      // Invalidates all queries that subscribe to this Artist `id` only.
      // `getArtist` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id }) => [{ type: "Artists", id }],
    }),
    deleteArtist: builder.mutation<void, string>({
      query: (id) => ({
        url: `artist/${id}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this Artist `id` only.
      // `getArtist` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, id) => [{ type: "Artists", id }],
    }),
    getArtists: builder.query<
      Array<ArtistClient>,
      Pick<ArtistsState, "search" | "sortBy" | "sortDirection">
    >({
      query: ({ search, sortBy, sortDirection }) => {
        const query = {
          // limit: perPage,
          // offset: rangeStart,
          sortBy,
          sortDirection,
          filter: { q: search },
        };
        return `artists?${stringify(query)}`;
      },
      providesTags: (result) => providesList(result, "Artists"),
    }),
    getUser: builder.query<UserClient, string>({
      query: (id) => {
        return `user/${id}`;
      },
      providesTags: (_result, _error, id) => [{ type: "Users", id }],
    }),
    getUsers: builder.query<
      Array<UserClient>,
      Pick<UsersState, "search" | "sortBy" | "sortDirection"> & {
        filter?: { artistId: string };
      }
    >({
      query: ({ filter, search, sortBy, sortDirection }) => {
        const query = {
          // limit: perPage,
          // offset: rangeStart,
          sortBy,
          sortDirection,
          filter: { ...filter, q: search },
        };
        return `users?${stringify(query)}`;
      },
      providesTags: (result) => providesList(result, "Users"),
    }),
    updateUser: builder.mutation<UserClient, Patch<UserClient>>({
      query: ({ id, ...patch }) => ({
        url: `user/${id}`,
        method: "PATCH",
        body: patch,
      }),
      // Invalidates all queries that subscribe to this User `id` only.
      // `getUsers` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id }) => [{ type: "Users", id }],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `user/${id}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this User `id` only.
      // `getUser` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, id) => [{ type: "Users", id }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useDeleteArtistMutation,
  useDeleteSongMutation,
  useDeleteUserMutation,
  useGetArtistQuery,
  useGetArtistsQuery,
  useGetSongQuery,
  useGetSongsQuery,
  useGetStatSongsQuery,
  useGetUserQuery,
  useGetUsersQuery,
  useUpdateArtistMutation,
  useUpdateSongMutation,
  useUpdateUserMutation,
} = adminApi;
